import { memo, useCallback, useMemo } from 'react';
import { FileTrigger } from 'react-aria-components';

import { Autosave, Button, DebouncedInput, Flex, Heading, Input, Label, Text, TextField } from '@/components/core';
import { ImportIcon } from '@/components/icons';
import { PROFILE_PICTURE_MIME_TYPES } from '@/constants';
import { useImageSrc } from '@/hooks';
import { toProfileId, useAutoUpdateProfile, usePutFile } from '@/hooks/api';
import { useProfileStore } from '@/store';

import { SettingsHeader } from './SettingsHeader';

export type ProfileDetailsProps = {
  profileId: string;
};

const IProfileDetails = ({ profileId }: ProfileDetailsProps) => {
  const givenName = useProfileStore((state) => state.givenName);
  const familyName = useProfileStore((state) => state.familyName);
  const email = useProfileStore((state) => state.email);
  const pictureUrl = useProfileStore((state) => state.pictureUrl);
  const { src } = useImageSrc({ source: pictureUrl });
  const updateProfile = useProfileStore((state) => state.updateProfile);
  const { mutate: putFile } = usePutFile();

  const updates = useMemo(() => {
    return { familyName, givenName };
  }, [givenName, familyName]);
  const { isPending, isSuccess, error } = useAutoUpdateProfile(1000, toProfileId('me'), updates);

  const onSelect = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || !pictureUrl) return;
      const files = Array.from(fileList);
      if (!files) return;
      putFile({ fileUrl: pictureUrl, file: files[0] });
    },
    [pictureUrl, putFile]
  );

  return (
    <>
      <SettingsHeader>
        <Flex col className="gap-xs grow">
          <Heading>Profile</Heading>
          <Text>Manage your personal details</Text>
        </Flex>

        <Autosave isPending={isPending} isSuccess={isSuccess} error={error} />
      </SettingsHeader>

      <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4">
        <Flex col className="gap-sm">
          <Label>Profile picture</Label>

          <Flex className="gap-sm">
            <img className="size-14 rounded-full" src={src} />

            <Flex col className="gap-xs">
              <FileTrigger allowsMultiple={false} acceptedFileTypes={PROFILE_PICTURE_MIME_TYPES} onSelect={onSelect}>
                <Button className="w-fit" variant="secondary">
                  <ImportIcon />
                  <Text>Upload new</Text>
                </Button>
              </FileTrigger>

              <Text size="xs">.jpeg, .png and .webp up to 10MB.</Text>
            </Flex>
          </Flex>
        </Flex>

        <div className="flex gap-2">
          <TextField className="flex w-1/2 flex-col gap-1" defaultValue={givenName ?? undefined}>
            <Label>First name *</Label>
            <DebouncedInput onChange={(e) => updateProfile({ givenName: e.target.value })} />
          </TextField>
          <TextField className="flex w-1/2 flex-col gap-1" defaultValue={familyName ?? undefined}>
            <Label>Last name *</Label>
            <DebouncedInput onChange={(e) => updateProfile({ familyName: e.target.value })} />
          </TextField>
        </div>

        <TextField className="flex flex-col gap-1" type="email" defaultValue={email ?? undefined} isDisabled>
          <Label>Email *</Label>
          <Input />
          <Text size="xs" slot="description">
            The email address associated with this account.
          </Text>
        </TextField>
      </div>
    </>
  );
};

export const ProfileDetails = memo(IProfileDetails) as typeof IProfileDetails;
